/* eslint-disable @nx/enforce-module-boundaries */
import { SkipLogicQuestionConfig } from '@hyper-inferno/skip-logic-core';

import {
  QuestionHandler,
  ITransformationContext,
  OPERATOR_FUNCTIONS,
} from '@hyper-inferno/survey-bot-core';
import {
  ComposedCondition,
  ConditionSet,
  QuestionType,
} from '@hyper-inferno/survey-elements-core';
import { ComposedElement } from '@hyper-inferno/survey-elements-core-utils';

export class SkipLogicHandler implements QuestionHandler<any> {
  async buildConfig(el: any, ctx: ITransformationContext) {
    return el.config;
  }

  getNextPageId(skipElement: ComposedElement, context: ITransformationContext) {
    const { bundle, session } = context;
    const rootConditonSet = bundle.getConditionSetsBySkipElementSettingId(
      (skipElement as any)[QuestionType.SKIP_LOGIC].id,
    );

    const isMatch = this.evaluateConditionSet(rootConditonSet, context);
    if (isMatch) {
      // 2. Logic matched! Map the UUID 'skip_logic_id' to a numeric Page ID
      // You'll need this helper method in your SurveyBundle class
      return ((skipElement as any)[QuestionType.SKIP_LOGIC] as SkipLogicQuestionConfig).page_id;
    }

    return null; // No conditions met for this element
  }
  private evaluateConditionSet(
    set: ConditionSet,
    context: ITransformationContext,
  ) {
    const results = (set?.conditions ?? []).map((condition) => {
      // Find the question being referenced (e.g., Q1)
      const targetElement = context.bundle.fetchElementById(
        condition.condition_element_id,
      );

      if (!targetElement) return false;

      // Get the handler for that question type (Single Choice, Numeric, etc.)
      const handler = context.handlers[targetElement.type];
      if (!handler || typeof handler.getValue !== 'function') {
        console.warn(`No getValue implementation for ${targetElement.type}`);
        return false;
      }

      // Get user's answer via the specific handler
      const userValue = handler.getValue(
        targetElement,
        context.session.jsn_responses[targetElement.id] ?? {},
      );

      // Execute the operator function
      const operatorFn = OPERATOR_FUNCTIONS[condition.operator];
      const composedCondition: ComposedCondition = {
        ...condition,
        row_key: condition.row_key,
        option_key: 0,
        element: targetElement,
        option_keys: [],
      };
      if (condition.option_id) {
        const option = context.bundle.fetchOptionById(condition.option_id);
        composedCondition.option_key = option?.option_key ?? 0;
      }

      if (condition.option_targets) {
        composedCondition.option_keys = condition.option_targets.map(
          (option_id) =>
            context.bundle.fetchOptionById(option_id)?.option_key ?? 0,
        );
      }

      return operatorFn ? operatorFn(userValue, composedCondition) : false;
    });

    // Handle AND / OR logic
    if (set.logical_operator === 'AND') {
      return results.every((res) => res === true);
    }
    return results.some((res) => res === true);
  }
}
